import { Link } from "react-router-dom";
import { Building2, BarChart3, Download, Users, Shield, ChevronRight } from "lucide-react";

export const GOLD = "#C9A227";
export const CHOCOLATE = "#3D2817";
export const GOLD_SOFT = "#D4AF37";
export const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

export const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

const KPI_ICON = {
    revenue: {
        bg: "#FDF3DC", color: "#C9A227", sub: "#3D2817",
        wash: "linear-gradient(150deg, #FFFCF6 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(201,162,39,0.14) 0%, transparent 52%)",
        accent: "#C9A227",
    },
    bookings: {
        bg: "#E0EDFA", color: "#3B82C4", sub: "#2E6BA8",
        wash: "linear-gradient(150deg, #F5F9FE 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(59,130,196,0.12) 0%, transparent 52%)",
        accent: "#3B82C4",
    },
    customers: {
        bg: "#FBE4EF", color: "#D9468F", sub: "#B83A72",
        wash: "linear-gradient(150deg, #FDF6F9 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(217,70,143,0.11) 0%, transparent 52%)",
        accent: "#D9468F",
    },
    staff: {
        bg: "#DCEEDC", color: "#2D9B6E", sub: "#1F7A55",
        wash: "linear-gradient(150deg, #F4FBF7 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(45,155,110,0.11) 0%, transparent 52%)",
        accent: "#2D9B6E",
    },
    services: {
        bg: "#FCE8E4", color: "#C84B31", sub: "#9A4A32",
        wash: "linear-gradient(150deg, #FFF8F6 0%, #FFFFFF 55%)",
        glow: "radial-gradient(circle at 100% 0%, rgba(200,75,49,0.12) 0%, transparent 52%)",
        accent: "#C84B31",
    },
};

export function GoldDivider({ className = "max-w-[220px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
            <span className="px-2.5 text-[10px] leading-none text-[#C9A227]">◆</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
        </div>
    );
}

export function PageHeader({ eyebrow, title, subtitle, action }) {
    return (
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
                <p className={EYEBROW} style={{ color: GOLD }}>
                    {eyebrow}
                </p>
                <h1 className="mt-1.5 text-[1.6rem] font-bold leading-snug text-[#4A0E17]">{title}</h1>
                {subtitle && <p className="mt-1 text-sm font-medium text-[#8A8178]">{subtitle}</p>}
                <GoldDivider className="mt-3 max-w-[260px]" />
            </div>
            {action}
        </header>
    );
}

export function StatCard({
    label,
    value,
    icon: Icon = BarChart3,
    sub,
    iconTone = "revenue",
    to,
    accent,
    iconColor,
}) {
    const theme = accent && iconColor
        ? {
            bg: accent,
            color: iconColor,
            sub: iconColor,
            wash: "linear-gradient(150deg, #FFFCF6 0%, #FFFFFF 55%)",
            glow: "radial-gradient(circle at 100% 0%, rgba(201,162,39,0.1) 0%, transparent 52%)",
            accent: iconColor,
        }
        : (KPI_ICON[iconTone] ?? KPI_ICON.revenue);

    const card = (
        <div
            className="group relative flex h-full min-h-[148px] flex-col overflow-hidden rounded-xl border border-[#E8E4DE]/80 p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D4AF37]/30 hover:shadow-[0_12px_32px_rgba(45,41,38,0.08)] sm:min-h-[156px] sm:p-6"
            style={{ background: theme.wash }}
        >
            <div
                className="pointer-events-none absolute -right-3 -top-3 size-24"
                style={{ background: theme.glow }}
                aria-hidden
            />
            <div
                className="absolute inset-x-0 top-0 h-[2px]"
                style={{
                    background: `linear-gradient(90deg, #4A0E17 0%, ${theme.accent} 50%, #4A0E17CC 100%)`,
                }}
                aria-hidden
            />

            <div className="relative flex shrink-0 items-start justify-between gap-2">
                <p className="min-h-[2.75rem] max-w-[calc(100%-2.75rem)] pt-0.5 text-[11px] font-extrabold uppercase leading-snug tracking-[0.14em] text-[#3D2817]">
                    {label}
                </p>
                <div
                    className="flex size-10 shrink-0 items-center justify-center rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] ring-1 ring-white/90 transition-transform duration-300 group-hover:scale-105"
                    style={{ backgroundColor: theme.bg }}
                >
                    <Icon className="size-[18px]" style={{ color: theme.color }} strokeWidth={2.25} />
                </div>
            </div>

            <p className="relative mt-3 shrink-0 truncate text-[1.875rem] font-extrabold leading-none tracking-tight tabular-nums text-[#3D2817] sm:text-[2rem]">
                {value}
            </p>

            <p
                className="relative mt-auto min-h-[1.25rem] truncate pt-2.5 text-[12px] font-extrabold tracking-wide sm:text-[13px]"
                style={{ color: sub ? theme.sub : "transparent" }}
            >
                {sub || "\u00A0"}
            </p>

            {to && (
                <ChevronRight className="absolute bottom-5 right-5 size-4 text-[#8A8178] opacity-0 transition-opacity group-hover:opacity-100 sm:bottom-6 sm:right-6" />
            )}
        </div>
    );

    if (!to) return card;

    return (
        <Link to={to} className="block h-full rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37]/60">
            {card}
        </Link>
    );
}

export const OVERVIEW_ICONS = {
    salons: Building2,
    active: BarChart3,
    revenue: Download,
    trials: BarChart3,
    users: Users,
    signups: Shield,
};
